import { VStack } from '@chakra-ui/react'
import { NodeProps } from '@xyflow/react'
import React from 'react'

import { Radio, RadioGroup } from '@mangovoice/mango-ui'

import { ChangeCallerIdNodeData } from '../../converters/shared/reactFlowTypes'
import { useSwitchboardT } from '../../types'
import { text } from '../../utils/text'
import { NodeInput } from '../core'
import { BaseNode, nodePropsAreEqual } from './BaseNode'
import { useChangeCallerIdNode } from './hooks/useChangeCallerIdNode'

const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation()

export interface ChangeCallerIdNodeProps extends NodeProps {
  onDelete?: () => void
  onDeleteNode?: (nodeId: string) => void

  showDeleteButton?: boolean
}

const ChangeCallerIdNodeComponent: React.FC<ChangeCallerIdNodeProps> = ({
  id,
  data,
  onDelete,
  onDeleteNode,
  showDeleteButton,
}) => {
  const nodeData = data as ChangeCallerIdNodeData & Record<string, unknown>
  const {
    currentCallerId,
    currentMode,
    currentMinimized,
    handleCallerIdChange,
    handleCallerIdBlur,
    handleModeChange,
    handleMinimizedChange,
    error,
    hasData,
  } = useChangeCallerIdNode(id, nodeData)
  const t = useSwitchboardT()
  const renderCallerIdInput = () => (
    <NodeInput
      label={text(t, 'switchboard.nodes.changeCallerId.callerId', 'Caller ID')}
      value={currentCallerId}
      onChange={(e) => handleCallerIdChange(e.target.value)}
      onBlur={handleCallerIdBlur}
      placeholder={text(
        t,
        'switchboard.nodes.changeCallerId.enterCallerId',
        'Enter caller ID',
      )}
      required
      error={error || undefined}
    />
  )

  return (
    <BaseNode
      label={text(
        t,
        'switchboard.nodeLabels.change_caller_id',
        'CHANGE CALLER ID',
      )}
      showPlaceholder={!hasData}
      onDelete={onDelete}
      onDeleteNode={onDeleteNode}
      id={id}
      showDeleteButton={showDeleteButton}
      minimized={currentMinimized}
      onMinimizeToggle={() => handleMinimizedChange(!currentMinimized)}
      minimizedContent={renderCallerIdInput()}
    >
      <VStack align="stretch" gap={3}>
        {renderCallerIdInput()}

        <RadioGroup
          className="nodrag"
          size="sm"
          value={currentMode}
          onValueChange={(details) => {
            if (details.value === 'add_front' || details.value === 'replace') {
              handleModeChange(details.value)
            }
          }}
          onMouseDown={stopPropagation}
          onPointerDown={stopPropagation}
          onClick={stopPropagation}
          onKeyDown={stopPropagation}
        >
          <VStack align="stretch" gap={2}>
            <Radio value="add_front">
              {text(
                t,
                'switchboard.nodes.changeCallerId.addUpTo20Chars',
                'Add up to 20 characters to the front',
              )}
            </Radio>
            <Radio value="replace">
              {text(
                t,
                'switchboard.nodes.changeCallerId.replaceCallerId',
                'Replace Caller ID',
              )}
            </Radio>
          </VStack>
        </RadioGroup>
      </VStack>
    </BaseNode>
  )
}

export const ChangeCallerIdNode = React.memo(
  ChangeCallerIdNodeComponent,
  nodePropsAreEqual,
)
